import { useQuery } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import axios from 'axios';
import { ScreeningQuestionResponses } from '@/types';

const GOOGLE_TRANSLATE_API_KEY = import.meta.env.VITE_GOOGLE_TRANSLATE_API_KEY;
const GOOGLE_TRANSLATE_BASE_URL = import.meta.env.VITE_GOOGLE_TRANSLATE_BASE_URL;

interface useGoogleTranslateProps {
  languageCode: string;
  initialAnswers: ScreeningQuestionResponses[];
  initialQuestion: string;
}

const useGoogleTranslate = ({
  languageCode,
  initialAnswers,
  initialQuestion,
}: useGoogleTranslateProps) => {
  const url = `${GOOGLE_TRANSLATE_BASE_URL}?key=${GOOGLE_TRANSLATE_API_KEY}`;
  const [batchedTextToTranslate, setBatchedTextToTranslate] = useState<string>('');

  const {
    data: translationResult,
    isPending,
    error,
  } = useQuery({
    queryKey: ['google-translate', batchedTextToTranslate, languageCode],
    queryFn: async () => {
      const response = await axios.post(url, {
        q: batchedTextToTranslate,
        target: languageCode,
        format: 'text',
      });
      return response.data.data.translations[0];
    },
    enabled: !!batchedTextToTranslate && !!languageCode,
  });

  const translateTextBatch = useCallback((text: string) => setBatchedTextToTranslate(text), []);

  // Extracting translated text and source language from the response
  const translatedText = translationResult?.translatedText || '';
  // Example format: ['Translated Question', 'Answer 1', 'Answer 2', ...]
  const splitTranslations = translatedText.split(' ~~S~~ ').map((item: string) => item.trim());
  const translatedQuestion = splitTranslations[0] || '';
  // Example format: [{ id: '1', originalText: 'Answer 1', translatedText: 'Translated Answer 1' }, ...]
  const screeningAnswers = initialAnswers
    ? initialAnswers.map(({ id, value }, i) => ({
        id,
        originalText: value,
        translatedText: error ? null : splitTranslations[i + 1],
      }))
    : [];

  return {
    screeningQuestion: error ? initialQuestion : translatedQuestion,
    screeningAnswers,
    error,
    translateTextBatch,
    isPending,
  };
};

export default useGoogleTranslate;
